import { useEffect, useMemo, useState } from "react";

// =============================================================================
// SubmitProposalPage — MM proposal submission form (LIQ-64 increment 2)
//
// New-stack rebuild of the legacy intake/static/index.html stepper against
// the /api/v1/intake API (LIQ-64 increment 1). Contract:
//   in:  GET  /api/v1/intake/rfp/{token}/info   (incl. dex_per_pool_constraints)
//   out: POST /api/v1/intake/rfp/{token}/submit (payload shape proven 201 on
//        2026-07-26: prop_484f1446, completeness 100%)
//
// Ratified form review (operator, 2026-07-23/26), all applied here:
//   - Venue Commitments: DEX venues get the five KPI families driven by the
//     RFP's per-pool constraints (visible bands only, spread floor shown);
//     depth is PER-SIDE everywhere — no TOTAL option; no Quoting Hours.
//   - Economic Terms: Token Loan / Engagement Term / Termination Notice above
//     model-specific fields; Profit Share model added; call option has no
//     "Option Size" (the loan IS the quantity); DEX inventory-as-ask section;
//     Additional Fees last.
//   - Transparency: API=YES asks only Type + Data Available.
//   - Stress step renders only when a CEX venue is committed.
//
// depth_semantics:"per_side" is emitted explicitly for CEX venues. MMs reach
// this form only after the increment-3 cutover, which ships the scoring-side
// per-side flip in the same motion — no label/scoring disagreement window.
// =============================================================================

const TEXT = "#cfe0ff", MUTED = "#8a93a6", AMBER = "#e0a458", GREEN = "#6fcf97",
  RED = "#e07070", ACCENT = "#6699ff", BORDER = "rgba(255,255,255,0.08)",
  CARD = "rgba(255,255,255,0.03)", BG = "#0a0e17";

const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10,
  padding: "20px 24px" } as const;
const lbl = { fontSize: 11, color: MUTED, textTransform: "uppercase",
  letterSpacing: 0.6, marginBottom: 6, display: "block" } as const;
const inp = { fontSize: 13, padding: "9px 12px", color: TEXT, borderRadius: 6,
  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)",
  width: "100%", boxSizing: "border-box" } as const;
const btn = { fontSize: 13, fontWeight: 700, padding: "9px 20px", borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.14)", background: "transparent",
  color: TEXT, cursor: "pointer" } as const;
const btnPrimary = { ...btn, background: ACCENT, color: "#0a0e17", border: "none" } as const;
const errTxt = { fontSize: 12, color: RED, marginTop: 4 } as const;
const hint = { fontSize: 11, color: MUTED, marginTop: 4 } as const;

type PoolConstraint = {
  venue: string; pair: string; fee_tier_bps: number; spread_floor_bps: number;
  depth_bands_bps: number[]; concentration_band_bps: number;
};
type TokenInfo = {
  rfp_id: string; token_symbol: string; mandated_venues: string[];
  permitted_compensation_models: string[]; expires_at: string; active: boolean;
  dex_per_pool_constraints?: PoolConstraint[]; venue_types?: string[];
};

type SpreadClip = { clip_usd: string; max_all_in_bps: string };
type DexVenueState = {
  selected: boolean;
  clips: SpreadClip[];
  depth: Record<string, { bid_usd: string; ask_usd: string }>;
  uptime_pct: string; tvl_floor_usd: string; concentration_min_pct: string;
  mev_protections: string; notes: string;
};
type CexVenueState = {
  selected: boolean;
  max_spread_bps: string; uptime_pct: string;
  d50: string; d100: string; d200: string; notes: string;
};

const STEPS = [
  { id: "identity", num: "01", title: "Proposer Identity" },
  { id: "venues", num: "02", title: "Venue Commitments" },
  { id: "stress", num: "03", title: "Stress Performance" },
  { id: "economics", num: "04", title: "Economic Terms" },
  { id: "transparency", num: "05", title: "Transparency & Reporting" },
  { id: "operational", num: "06", title: "Operational Background" },
  { id: "narrative", num: "07", title: "Narrative" },
  { id: "review", num: "08", title: "Review & Submit" },
];

const emptyDexVenue = (bands: number[]): DexVenueState => ({
  selected: true,
  clips: [{ clip_usd: "", max_all_in_bps: "" }],
  depth: Object.fromEntries(bands.map((b) => [String(b), { bid_usd: "", ask_usd: "" }])),
  uptime_pct: "", tvl_floor_usd: "", concentration_min_pct: "",
  mev_protections: "", notes: "",
});
const emptyCexVenue = (): CexVenueState => ({
  selected: true, max_spread_bps: "", uptime_pct: "",
  d50: "", d100: "", d200: "", notes: "",
});

const num = (s: string): number | undefined => {
  if (s === "" || s === null || s === undefined) return undefined;
  const v = Number(s);
  return Number.isFinite(v) ? v : undefined;
};
const intOr = (s: string): number | undefined => {
  const v = num(s);
  return v === undefined ? undefined : Math.round(v);
};

// Module scope: defining this inside the component created a fresh component
// identity per render, forcing React to remount every input on each keystroke
// (focus loss). It closes over module constants only, so it lives out here.
const Field = ({ label, children, note }: { label: string; children: any; note?: string }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={lbl}>{label}</label>
    {children}
    {note ? <div style={hint}>{note}</div> : null}
  </div>
);

export function SubmitProposalPage({ token }: { token: string }) {
  const [info, setInfo] = useState<TokenInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  // LIQ-120: profile prefill (firm-level steps hydrated from the entity registry)
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  const [updateProfile, setUpdateProfile] = useState(true);

  // 01 identity
  const [entity, setEntity] = useState(""); const [contact, setContact] = useState("");
  const [email, setEmail] = useState(""); const [jurisdiction, setJurisdiction] = useState("");
  // 02 venues
  const [dexV, setDexV] = useState<Record<string, DexVenueState>>({});
  const [cexV, setCexV] = useState<Record<string, CexVenueState>>({});
  // 03 stress (CEX only)
  const [stressExp, setStressExp] = useState(""); const [stressRet, setStressRet] = useState("");
  const [stressRec, setStressRec] = useState(""); const [stressCond, setStressCond] = useState("");
  // 04 economics — ratified ordering: loan/term/notice above model fields
  const [tokenLoan, setTokenLoan] = useState("");
  const [engTerm, setEngTerm] = useState(""); const [termNotice, setTermNotice] = useState("");
  const [compModel, setCompModel] = useState("");
  const [retainerUsd, setRetainerUsd] = useState("");
  const [optPremiumPct, setOptPremiumPct] = useState("");
  const [strikeDeriv, setStrikeDeriv] = useState("");
  const [numTranches, setNumTranches] = useState(""); const [trancheLen, setTrancheLen] = useState("");
  const [psSplit, setPsSplit] = useState(""); const [psHurdle, setPsHurdle] = useState("");
  const [psIlResp, setPsIlResp] = useState(""); const [psIlShared, setPsIlShared] = useState("");
  const [psIlBench, setPsIlBench] = useState(""); const [psPerfFee, setPsPerfFee] = useState("");
  const [psFloor, setPsFloor] = useState("");
  const [quoteReq, setQuoteReq] = useState(false); const [quoteLoan, setQuoteLoan] = useState("");
  const [ownCap, setOwnCap] = useState(false); const [ownCapUsd, setOwnCapUsd] = useState("");
  const [ownCapComp, setOwnCapComp] = useState(""); const [ownCapLock, setOwnCapLock] = useState("");
  const [addlFees, setAddlFees] = useState("");
  // 05 transparency & reporting
  const [apiOffered, setApiOffered] = useState<boolean | null>(null);
  const [apiType, setApiType] = useState("");
  const [apiData, setApiData] = useState<string[]>([]);
  const [dataFormats, setDataFormats] = useState<string[]>(["json"]);
  const [repFreq, setRepFreq] = useState(""); const [repContents, setRepContents] = useState("");
  const [repChecks, setRepChecks] = useState<string[]>([]);
  const [apiNotes, setApiNotes] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("");
  // 06 operational
  const [infra, setInfra] = useState(""); const [risk, setRisk] = useState("");
  const [years, setYears] = useState(""); const [teamSize, setTeamSize] = useState("");
  const [cexCovered, setCexCovered] = useState(""); const [dexCovered, setDexCovered] = useState("");
  const [regStatus, setRegStatus] = useState("");
  // 07 narrative
  const [narrSummary, setNarrSummary] = useState(""); const [narrApproach, setNarrApproach] = useState("");
  const [narrRisk, setNarrRisk] = useState(""); const [narrValue, setNarrValue] = useState("");
  // submit
  // Browser/swipe back moves between steps instead of leaving the form.
  useEffect(() => {
    window.history.replaceState({ lgeStep: 0 }, "");
    const onPop = (e: PopStateEvent) => {
      if (e.state && typeof e.state.lgeStep === "number") setStep(e.state.lgeStep);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const gotoStep = (i: number) => {
    window.history.pushState({ lgeStep: i }, "");
    setStep(i);
  };

  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [rejection, setRejection] = useState<any[] | null>(null);
  const [accepted, setAccepted] = useState<any | null>(null);

  // ---------------------------------------------------------------------
  // Load token info; initialize venue cards from mandated_venues
  // ---------------------------------------------------------------------
  useEffect(() => {
    fetch(`/api/v1/intake/rfp/${encodeURIComponent(token)}/info`)
      .then(async (r) => {
        const b = await r.json();
        if (!r.ok) throw new Error(b.error ?? `Failed (${r.status})`);
        return b as TokenInfo;
      })
      .then((b) => {
        setInfo(b);
        const cons = b.dex_per_pool_constraints ?? [];
        const dex: Record<string, DexVenueState> = {};
        const cex: Record<string, CexVenueState> = {};
        for (const mv of b.mandated_venues ?? []) {
          const c = cons.find((pc) => `${pc.venue}|${pc.pair}` === mv);
          if (c) dex[mv] = emptyDexVenue(c.depth_bands_bps);
          else cex[mv] = emptyCexVenue();
        }
        setDexV(dex); setCexV(cex);
      })
      .catch((e: unknown) => setLoadErr(e instanceof Error ? e.message : "Failed to load RFP"));
  }, [token]);

  const constraints = useMemo(() => {
    const m: Record<string, PoolConstraint> = {};
    for (const c of info?.dex_per_pool_constraints ?? []) m[`${c.venue}|${c.pair}`] = c;
    return m;
  }, [info]);

  const selectedDex = Object.entries(dexV).filter(([, v]) => v.selected);
  const selectedCex = Object.entries(cexV).filter(([, v]) => v.selected);
  const hasCex = selectedCex.length > 0;
  const hasDex = selectedDex.length > 0;
  const visibleSteps = STEPS.filter((s) => s.id !== "stress" || hasCex);
  const stepObj = visibleSteps[Math.min(step, visibleSteps.length - 1)];

  // LIQ-120: hydrate steps 01 (identity), 05 (transparency & reporting) and
  // 06 (operational) from info.profile_prefill — attached by /info when the
  // submission token's market maker resolves in the entity registry. Runs
  // once; deal-level steps (02/03/04/07) are never touched. The reverse map
  // mirrors buildPayload() field-for-field.
  useEffect(() => {
    const p = (info as any)?.profile_prefill;
    if (!p || prefilledFrom !== null) return;
    const pr = p.proposer || {};
    if (pr.entity_name) setEntity(pr.entity_name);
    if (pr.contact_name) setContact(pr.contact_name);
    if (pr.contact_email) setEmail(pr.contact_email);
    if (pr.jurisdiction) setJurisdiction(pr.jurisdiction);

    const od = p.operational_details || {};
    if (od.infrastructure) setInfra(od.infrastructure);
    if (od.risk_management) setRisk(od.risk_management);
    if (od.team_size != null) setTeamSize(String(od.team_size));
    if (od.regulatory_status) setRegStatus(od.regulatory_status);
    const ex = od.experience || {};
    if (ex.years_active != null) setYears(String(ex.years_active));
    if (Array.isArray(ex.exchanges_active) && ex.exchanges_active.length) setCexCovered(ex.exchanges_active.join(", "));
    if (Array.isArray(ex.dex_venues_active) && ex.dex_venues_active.length) setDexCovered(ex.dex_venues_active.join(", "));

    const tr = p.transparency || {};
    const api = tr.api_access || {};
    if (typeof api.offered === "boolean") setApiOffered(api.offered);
    if (api.type) setApiType(api.type);
    if (Array.isArray(api.data_available) && api.data_available.length) setApiData(api.data_available);
    if (api.endpoint_description) setApiNotes(api.endpoint_description);
    if (api.offered === false) {
      const rc = p.reporting_commitment || {};
      const df: string[] = Array.isArray(tr.data_formats) ? tr.data_formats : [];
      if (df.length === 1 && df[0] === "dashboard") setDeliveryMethod("dashboard");
      else if (df.length) setDataFormats(df);
      if (rc.delivery_method) setDeliveryMethod(rc.delivery_method);
      if (rc.delivery_timeline) setRepFreq(rc.delivery_timeline);
      // stored contents round-trip through the free-text lines; buildPayload
      // concatenates checkbox + lines into the same `contents` list
      if (Array.isArray(rc.contents) && rc.contents.length) setRepContents(rc.contents.join("\n"));
    }
    setPrefilledFrom((info as any)?.entity?.market_maker_name || pr.entity_name || "your");
  }, [info]);

  const permittedModels = info?.permitted_compensation_models?.length
    ? info.permitted_compensation_models : ["retainer", "call_option"];

  // ---------------------------------------------------------------------
  // Payload assembly — MUST match the shape proven 201 against the intake
  // pipeline (2026-07-26 smoke) exactly.
  // ---------------------------------------------------------------------
  const buildPayload = () => {
    const pvc: Record<string, any> = {};
    const committed: string[] = [];
    for (const [key, v] of selectedCex) {
      committed.push(key);
      pvc[key] = {
        max_spread_bps: num(v.max_spread_bps),
        uptime_pct: num(v.uptime_pct),
        depth: { "50bps": num(v.d50), "100bps": num(v.d100), "200bps": num(v.d200) },
        depth_semantics: "per_side",
        notes: v.notes || undefined,
      };
    }
    for (const [key, v] of selectedDex) {
      committed.push(key);
      const c = constraints[key];
      pvc[key] = {
        instrument_type: "dex",
        pair: c?.pair,
        dex_commitments: {
          spread_commitments: v.clips
            .filter((cl) => cl.clip_usd !== "" || cl.max_all_in_bps !== "")
            .map((cl) => ({ clip_usd: num(cl.clip_usd), max_all_in_bps: num(cl.max_all_in_bps) })),
          depth: Object.fromEntries(Object.entries(v.depth).map(([b, d]) =>
            [b, { bid_usd: num(d.bid_usd), ask_usd: num(d.ask_usd) }])),
          uptime_pct: num(v.uptime_pct),
          tvl_floor_usd: num(v.tvl_floor_usd),
          concentration_min_pct: num(v.concentration_min_pct),
          mev_protections: v.mev_protections || undefined,
        },
        notes: v.notes || undefined,
      };
    }
    const uptimes = [
      ...selectedCex.map(([, v]) => num(v.uptime_pct)),
      ...selectedDex.map(([, v]) => num(v.uptime_pct)),
    ].filter((x): x is number => x !== undefined);

    const econ: any = {
      compensation_model: compModel || undefined,
      token_loan_request: intOr(tokenLoan),
      engagement_term_months: num(engTerm),
      termination_notice_days: num(termNotice),
      additional_fees: addlFees || undefined,
    };
    if (compModel === "retainer") econ.monthly_retainer_usd = num(retainerUsd);
    if (compModel === "call_option") {
      econ.option_premium_pct = num(optPremiumPct);
      econ.strike_price_derivation = strikeDeriv || undefined;
      econ.num_tranches = intOr(numTranches);
      econ.tranche_length_months = num(trancheLen);
    }
    if (compModel === "profit_share") {
      econ.profit_share = {
        fee_split_client_pct: num(psSplit),
        hurdle_rate_pct: num(psHurdle),
        il_responsibility: psIlResp || undefined,
        il_shared_client_pct: psIlResp === "shared" ? num(psIlShared) : undefined,
        il_benchmark: psIlBench || undefined,
        perf_fee_quote_accum_pct: num(psPerfFee),
        retainer_floor_usd: num(psFloor),
      };
    }
    if (hasDex) {
      econ.inventory_request = {
        base_loan_tokens: intOr(tokenLoan),
        quote_required: quoteReq,
        quote_loan_amount_usd: quoteReq ? num(quoteLoan) : undefined,
        own_capital_committed: ownCap,
        own_capital: ownCap ? {
          amount_usd: num(ownCapUsd), composition: ownCapComp || undefined,
          lockup_months: num(ownCapLock),
        } : undefined,
      };
    }

    const payload: any = {
      proposer: {
        entity_name: entity.trim(), contact_name: contact || undefined,
        contact_email: email || undefined, jurisdiction: jurisdiction || undefined,
      },
      committed_venues: committed,
      committed_uptime_pct: uptimes.length ? Math.min(...uptimes) : undefined,
      per_venue_commitments: pvc,
      economic_terms: econ,
      transparency: {
        api_access: {
          offered: apiOffered === true,
          type: apiOffered ? (apiType || undefined) : undefined,
          data_available: apiOffered ? apiData : [],
          endpoint_description: apiOffered ? (apiNotes || undefined) : undefined,
        },
        // API offered: the API is the format; dashboard delivery likewise
        // self-describes. File-based delivery records the chosen formats.
        data_formats: apiOffered ? ["json"]
          : deliveryMethod === "dashboard" ? ["dashboard"] : dataFormats,
      },
      reporting_commitment: {
        // API offered => mandate verifies from the API directly; periodic
        // reporting not requested (explicit false, not omitted). Daily
        // implies weekly+monthly; on-demand sets neither — granularity is
        // preserved in delivery_timeline.
        weekly: apiOffered === false && (repFreq === "daily" || repFreq === "weekly"),
        monthly: apiOffered === false && ["daily", "weekly", "monthly"].includes(repFreq),
        delivery_method: apiOffered === false ? (deliveryMethod || undefined) : undefined,
        delivery_timeline: apiOffered === false ? (repFreq || undefined) : undefined,
        contents: apiOffered === false
          ? [...repChecks,
             ...(repContents ? repContents.split("\n").map((s) => s.trim()).filter(Boolean) : [])]
          : [],
      },
      operational_details: {
        infrastructure: infra || undefined, risk_management: risk || undefined,
        team_size: intOr(teamSize),
        regulatory_status: regStatus || undefined,
        experience: {
          years_active: num(years),
          exchanges_active: cexCovered ? cexCovered.split(",").map((s) => s.trim()).filter(Boolean) : [],
          dex_venues_active: dexCovered ? dexCovered.split(",").map((s) => s.trim()).filter(Boolean) : [],
        },
      },
      narrative_sections: {
        executive_summary: narrSummary || undefined,
        approach_description: narrApproach || undefined,
        risk_assessment: narrRisk || undefined,
        value_proposition: narrValue || undefined,
      },
    };
    if (hasCex) {
      payload.committed_stress = {
        max_spread_expansion_factor: num(stressExp),
        min_depth_retention_pct: num(stressRet),
        recovery_time_minutes: num(stressRec),
        conditions: stressCond || undefined,
      };
    }
    return payload;
  };

  // ---------------------------------------------------------------------
  // Client-side validation per step (mirrors PV*/PV17/PV19 + fee floors)
  // ---------------------------------------------------------------------
  const stepErrors = (id: string): string[] => {
    const errs: string[] = [];
    if (id === "identity") {
      if (!entity.trim()) errs.push("Entity name is required.");
    }
    if (id === "venues") {
      if (!hasCex && !hasDex) errs.push("Commit to at least one venue.");
      for (const [key, v] of selectedCex) {
        const short = key.split("|")[0];
        if (num(v.max_spread_bps) === undefined || (num(v.max_spread_bps) as number) <= 0)
          errs.push(`${short}: max spread (bps) must be > 0.`);
        const up = num(v.uptime_pct);
        if (up === undefined || up < 0 || up > 100) errs.push(`${short}: uptime must be 0–100%.`);
        for (const [b, val] of [["±50bps", v.d50], ["±100bps", v.d100], ["±200bps", v.d200]] as const) {
          const d = num(val);
          if (d === undefined || d <= 0) errs.push(`${short}: per-side depth at ${b} required.`);
        }
      }
      for (const [key, v] of selectedDex) {
        const c = constraints[key]; const short = key.split("|")[0];
        const clips = v.clips.filter((cl) => cl.clip_usd !== "" || cl.max_all_in_bps !== "");
        if (clips.length === 0) errs.push(`${short}: at least one spread commitment (clip size + all-in bps).`);
        for (const cl of clips) {
          const cu = num(cl.clip_usd), mb = num(cl.max_all_in_bps);
          if (cu === undefined || cu <= 0) errs.push(`${short}: clip size must be > 0 USD.`);
          if (mb === undefined || mb <= 0) errs.push(`${short}: all-in spread must be > 0 bps.`);
          else if (c && mb < c.spread_floor_bps)
            errs.push(`${short}: all-in ${mb} bps is below the pool fee tier (${c.spread_floor_bps} bps) — structurally infeasible on a CLAMM.`);
        }
        for (const [b, d] of Object.entries(v.depth)) {
          if (num(d.bid_usd) === undefined || (num(d.bid_usd) as number) <= 0 ||
              num(d.ask_usd) === undefined || (num(d.ask_usd) as number) <= 0)
            errs.push(`${short}: bid and ask depth at ±${b}bps required (per side).`);
        }
        const up = num(v.uptime_pct);
        if (up === undefined || up < 0 || up > 100) errs.push(`${short}: in-range uptime must be 0–100%.`);
        if (num(v.tvl_floor_usd) === undefined || (num(v.tvl_floor_usd) as number) < 0)
          errs.push(`${short}: TVL floor (USD) required.`);
        const cp = num(v.concentration_min_pct);
        if (cp === undefined || cp <= 0 || cp > 100)
          errs.push(`${short}: active-range concentration must be in (0, 100]%.`);
      }
    }
    if (id === "stress" && hasCex) {
      const e = num(stressExp);
      if (e !== undefined && e < 1) errs.push("Spread expansion factor must be ≥ 1.0.");
      const r = num(stressRet);
      if (r !== undefined && (r < 0 || r > 100)) errs.push("Depth retention must be 0–100%.");
    }
    if (id === "economics") {
      if (!compModel) errs.push("Select a compensation model.");
      if (tokenLoan !== "" && (intOr(tokenLoan) as number) < 0) errs.push("Token loan must be ≥ 0.");
      if (compModel === "retainer" && (num(retainerUsd) === undefined || (num(retainerUsd) as number) <= 0))
        errs.push("Monthly retainer (USD) required for the retainer model.");
      if (compModel === "call_option" && num(optPremiumPct) === undefined)
        errs.push("Option premium (%) required for the call option model.");
      if (compModel === "profit_share") {
        const s = num(psSplit);
        if (s === undefined || s <= 0 || s >= 100) errs.push("Fee split to client must be in (0, 100)%.");
        if (!psIlResp) errs.push("Select impermanent-loss responsibility.");
        if (psIlResp === "shared") {
          const sh = num(psIlShared);
          if (sh === undefined || sh < 0 || sh > 100) errs.push("Shared IL: client share must be 0–100%.");
        }
      }
      if (hasDex && quoteReq && (num(quoteLoan) === undefined || (num(quoteLoan) as number) <= 0))
        errs.push("Quote-side loan amount required when quote inventory is requested.");
      if (hasDex && ownCap && (num(ownCapUsd) === undefined || (num(ownCapUsd) as number) <= 0))
        errs.push("Own-capital amount required when committing own capital.");
    }
    if (id === "transparency") {
      if (apiOffered === null) errs.push("Answer whether read-only API access is offered.");
      if (apiOffered && !apiType) errs.push("Select the API access type.");
      if (apiOffered === false && !repFreq) errs.push("Select a reporting frequency.");
      if (apiOffered === false && deliveryMethod !== "dashboard" && dataFormats.length === 0) errs.push("Select at least one data format.");
    }
    return errs;
  };

  const allErrors = visibleSteps.flatMap((s) => s.id === "review" ? [] :
    stepErrors(s.id).map((e) => ({ step: s.title, msg: e })));

  const doSubmit = async () => {
    setBusy(true); setSubmitErr(null); setRejection(null);
    try {
      const r = await fetch(`/api/v1/intake/rfp/${encodeURIComponent(token)}/submit${prefilledFrom && updateProfile ? "?update_profile=1" : ""}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const b = await r.json();
      if (r.status === 201) { setAccepted(b); window.scrollTo(0, 0); }
      else if (r.status === 422 && b.errors) setRejection(b.errors);
      else setSubmitErr(b.error ?? `Submission failed (${r.status}).`);
    } catch { setSubmitErr("Network error — submission not sent."); }
    finally { setBusy(false); }
  };

  // ---------------------------------------------------------------------
  // Draft save / restore / download / import
  // ---------------------------------------------------------------------
  const draftKey = `lge_draft_${token}`;
  const snapshot = () => ({
    entity, contact, email, jurisdiction, dexV, cexV, stressExp, stressRet,
    stressRec, stressCond, tokenLoan, engTerm, termNotice, compModel,
    retainerUsd, optPremiumPct, strikeDeriv, numTranches, trancheLen, psSplit,
    psHurdle, psIlResp, psIlShared, psIlBench, psPerfFee, psFloor, quoteReq,
    quoteLoan, ownCap, ownCapUsd, ownCapComp, ownCapLock, addlFees, apiOffered,
    apiType, apiData, apiNotes, deliveryMethod, dataFormats, repFreq, repContents, infra, risk, years,
    teamSize, cexCovered, dexCovered, repChecks, regStatus, narrSummary, narrApproach, narrRisk,
    narrValue,
  });
  const restore = (s: any) => {
    setEntity(s.entity ?? ""); setContact(s.contact ?? ""); setEmail(s.email ?? "");
    setJurisdiction(s.jurisdiction ?? "");
    if (s.dexV) setDexV(s.dexV); if (s.cexV) setCexV(s.cexV);
    setStressExp(s.stressExp ?? ""); setStressRet(s.stressRet ?? "");
    setStressRec(s.stressRec ?? ""); setStressCond(s.stressCond ?? "");
    setTokenLoan(s.tokenLoan ?? ""); setEngTerm(s.engTerm ?? "");
    setTermNotice(s.termNotice ?? ""); setCompModel(s.compModel ?? "");
    setRetainerUsd(s.retainerUsd ?? ""); setOptPremiumPct(s.optPremiumPct ?? "");
    setStrikeDeriv(s.strikeDeriv ?? ""); setNumTranches(s.numTranches ?? "");
    setTrancheLen(s.trancheLen ?? ""); setPsSplit(s.psSplit ?? "");
    setPsHurdle(s.psHurdle ?? ""); setPsIlResp(s.psIlResp ?? "");
    setPsIlShared(s.psIlShared ?? ""); setPsIlBench(s.psIlBench ?? "");
    setPsPerfFee(s.psPerfFee ?? ""); setPsFloor(s.psFloor ?? "");
    setQuoteReq(!!s.quoteReq); setQuoteLoan(s.quoteLoan ?? "");
    setOwnCap(!!s.ownCap); setOwnCapUsd(s.ownCapUsd ?? "");
    setOwnCapComp(s.ownCapComp ?? ""); setOwnCapLock(s.ownCapLock ?? "");
    setAddlFees(s.addlFees ?? ""); setApiOffered(s.apiOffered ?? null);
    setApiType(s.apiType ?? ""); setApiData(s.apiData ?? []);
    setApiNotes(s.apiNotes ?? ""); setDeliveryMethod(s.deliveryMethod ?? "");
    setDataFormats(s.dataFormats ?? ["json"]); setRepFreq(s.repFreq ?? "");
    setRepContents(s.repContents ?? ""); setInfra(s.infra ?? "");
    setRisk(s.risk ?? ""); setYears(s.years ?? ""); setTeamSize(s.teamSize ?? "");
    setCexCovered(s.cexCovered ?? ""); setDexCovered(s.dexCovered ?? "");
    setRepChecks(s.repChecks ?? []); setRegStatus(s.regStatus ?? "");
    setNarrSummary(s.narrSummary ?? ""); setNarrApproach(s.narrApproach ?? "");
    setNarrRisk(s.narrRisk ?? ""); setNarrValue(s.narrValue ?? "");
  };
  const [draftNote, setDraftNote] = useState("");
  const saveDraft = () => {
    try { localStorage.setItem(draftKey, JSON.stringify(snapshot()));
      setDraftNote("Draft saved locally."); } catch { setDraftNote("Draft save failed."); }
    setTimeout(() => setDraftNote(""), 2500);
  };
  useEffect(() => {
    try { const s = localStorage.getItem(draftKey);
      if (s) { restore(JSON.parse(s)); setDraftNote("Draft restored."); setTimeout(() => setDraftNote(""), 2500); }
    } catch { /* fresh form */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(buildPayload(), null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `proposal_${info?.rfp_id ?? token}.json`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  // ---------------------------------------------------------------------
  // Small render helpers
  // ---------------------------------------------------------------------
  const Txt = (v: string, set: (s: string) => void, ph = "", type = "text") => (
    <input style={inp} value={v} type={type} placeholder={ph}
      onChange={(e) => set(e.target.value)} />
  );
  const Area = (v: string, set: (s: string) => void, rows = 4, ph = "") => (
    <textarea style={{ ...inp, resize: "vertical" }} rows={rows} value={v}
      placeholder={ph} onChange={(e) => set(e.target.value)} />
  );
  const Radio = (name: string, value: string, cur: string, set: (s: string) => void, label: string) => (
    <label key={value} style={{ display: "flex", alignItems: "center", gap: 8,
      fontSize: 13, color: TEXT, cursor: "pointer", padding: "6px 0" }}>
      <input type="radio" name={name} checked={cur === value} onChange={() => set(value)} />
      {label}
    </label>
  );
  const Check = (checked: boolean, toggle: () => void, label: string) => (
    <label key={label} style={{ display: "flex", alignItems: "center", gap: 8,
      fontSize: 13, color: TEXT, cursor: "pointer", padding: "4px 0" }}>
      <input type="checkbox" checked={checked} onChange={toggle} />
      {label}
    </label>
  );
  // Phone widths (src/mobile.css): the fixed two/three-column rows become one column.
  const grid2 = { display: "grid", gridTemplateColumns: "var(--m-form-cols, 1fr 1fr)", gap: 14 } as const;
  const grid3 = { display: "grid", gridTemplateColumns: "var(--m-form-cols, 1fr 1fr 1fr)", gap: 14 } as const;

  // ---------------------------------------------------------------------
  // Step bodies
  // ---------------------------------------------------------------------
  const renderIdentity = () => (
    <div style={card}>
      <div style={grid2}>
        <Field label="Entity Name *">{Txt(entity, setEntity, "AlphaMM LLC")}</Field>
        <Field label="Contact Name">{Txt(contact, setContact)}</Field>
        <Field label="Contact Email">{Txt(email, setEmail, "", "email")}</Field>
        <Field label="Jurisdiction">{Txt(jurisdiction, setJurisdiction, "e.g. Cayman Islands")}</Field>
      </div>
    </div>
  );

  const renderCexCard = (key: string, v: CexVenueState) => {
    const short = key.split("|")[0]; const pair = key.split("|")[1] ?? "";
    return (
      <div key={key} style={{ ...card, marginBottom: 16, opacity: v.selected ? 1 : 0.45 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>
            {short} <span style={{ color: MUTED, fontWeight: 400 }}>{pair}</span>
            <span style={{ marginLeft: 10, fontSize: 10, color: ACCENT, border: `1px solid ${ACCENT}`,
              borderRadius: 4, padding: "2px 6px", letterSpacing: 0.5 }}>CEX</span>
          </div>
          {Check(v.selected, () => setCexV({ ...cexV, [key]: { ...v, selected: !v.selected } }), "Commit")}
        </div>
        {v.selected && (<>
          <div style={grid2}>
            <Field label="Max Quoted Spread (bps)">{Txt(v.max_spread_bps,
              (s) => setCexV({ ...cexV, [key]: { ...v, max_spread_bps: s } }), "25")}</Field>
            <Field label="Uptime (%)">{Txt(v.uptime_pct,
              (s) => setCexV({ ...cexV, [key]: { ...v, uptime_pct: s } }), "97")}</Field>
          </div>
          <label style={lbl}>Committed Depth — USD per side, at each distance from mid</label>
          <div style={grid3}>
            <Field label="±50 bps">{Txt(v.d50, (s) => setCexV({ ...cexV, [key]: { ...v, d50: s } }), "25000")}</Field>
            <Field label="±100 bps">{Txt(v.d100, (s) => setCexV({ ...cexV, [key]: { ...v, d100: s } }), "50000")}</Field>
            <Field label="±200 bps">{Txt(v.d200, (s) => setCexV({ ...cexV, [key]: { ...v, d200: s } }), "100000")}</Field>
          </div>
          <Field label="Notes (optional)">{Area(v.notes, (s) => setCexV({ ...cexV, [key]: { ...v, notes: s } }), 2)}</Field>
        </>)}
      </div>
    );
  };

  const renderDexCard = (key: string, v: DexVenueState) => {
    const c = constraints[key]; const short = key.split("|")[0];
    const setV = (nv: DexVenueState) => setDexV({ ...dexV, [key]: nv });
    return (
      <div key={key} style={{ ...card, marginBottom: 16, opacity: v.selected ? 1 : 0.45 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>
            {short} <span style={{ color: MUTED, fontWeight: 400 }}>{c?.pair}</span>
            <span style={{ marginLeft: 10, fontSize: 10, color: AMBER, border: `1px solid ${AMBER}`,
              borderRadius: 4, padding: "2px 6px", letterSpacing: 0.5 }}>DEX · CLAMM</span>
          </div>
          {Check(v.selected, () => setV({ ...v, selected: !v.selected }), "Commit")}
        </div>
        {c && <div style={{ fontSize: 11, color: MUTED, marginBottom: 14 }}>
          Pool fee tier {c.fee_tier_bps} bps — all-in spread commitments below {c.spread_floor_bps} bps
          are structurally infeasible and will be rejected.
        </div>}
        {v.selected && (<>
          <label style={lbl}>1 · Effective Spread — max all-in cost (fee + price impact) at your committed clip sizes</label>
          {v.clips.map((cl, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 40px", gap: 14, marginBottom: 8 }}>
              <input style={inp} placeholder="Clip size (USD), e.g. 1000" value={cl.clip_usd}
                onChange={(e) => { const clips = [...v.clips]; clips[i] = { ...cl, clip_usd: e.target.value }; setV({ ...v, clips }); }} />
              <input style={inp} placeholder={`Max all-in (bps), ≥ ${c?.spread_floor_bps ?? "fee"}`} value={cl.max_all_in_bps}
                onChange={(e) => { const clips = [...v.clips]; clips[i] = { ...cl, max_all_in_bps: e.target.value }; setV({ ...v, clips }); }} />
              <button style={{ ...btn, padding: "6px 10px" }} title="Remove"
                onClick={() => setV({ ...v, clips: v.clips.filter((_, j) => j !== i) })}>×</button>
            </div>
          ))}
          <button style={{ ...btn, marginBottom: 16 }}
            onClick={() => setV({ ...v, clips: [...v.clips, { clip_usd: "", max_all_in_bps: "" }] })}>
            + Add clip size</button>

          <label style={lbl}>2 · Depth — USD per side within each band (bands per the RFP; ±{c ? Math.min(...c.depth_bands_bps) : 50}bps
            suppressed below the fee tier are not shown)</label>
          {Object.entries(v.depth).map(([b, d]) => (
            <div key={b} style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr", gap: 14, marginBottom: 8, alignItems: "center" }}>
              <div style={{ fontSize: 12, color: MUTED }}>±{b} bps</div>
              <input style={inp} placeholder="Bid side (USD)" value={d.bid_usd}
                onChange={(e) => setV({ ...v, depth: { ...v.depth, [b]: { ...d, bid_usd: e.target.value } } })} />
              <input style={inp} placeholder="Ask side (USD)" value={d.ask_usd}
                onChange={(e) => setV({ ...v, depth: { ...v.depth, [b]: { ...d, ask_usd: e.target.value } } })} />
            </div>
          ))}

          <div style={{ ...grid3, marginTop: 16 }}>
            <Field label="3 · In-Range Uptime (%)"
              note="Time with an active position spanning the pool price, with committed depth present. Chain halts excluded.">
              {Txt(v.uptime_pct, (s) => setV({ ...v, uptime_pct: s }), "95")}</Field>
            <Field label="4 · TVL Floor (USD)"
              note="Minimum position value you will maintain in the pool.">
              {Txt(v.tvl_floor_usd, (s) => setV({ ...v, tvl_floor_usd: s }), "120000")}</Field>
            <Field label={`5 · Concentration (% within ±${c?.concentration_band_bps ?? 200}bps)`}
              note="Share of your position's liquidity inside the band around current price.">
              {Txt(v.concentration_min_pct, (s) => setV({ ...v, concentration_min_pct: s }), "40")}</Field>
          </div>
          <Field label="MEV Protections (free text)"
            note="JIT posture, private orderflow, rebalance execution — scored qualitatively.">
            {Area(v.mev_protections, (s) => setV({ ...v, mev_protections: s }), 3)}</Field>
          <Field label="Notes (optional)">{Area(v.notes, (s) => setV({ ...v, notes: s }), 2)}</Field>
        </>)}
      </div>
    );
  };

  const renderVenues = () => (
    <div>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
        All depth figures are <span style={{ color: TEXT, fontWeight: 700 }}>per side</span> of the book or pool.
      </div>
      {Object.entries(cexV).map(([k, v]) => renderCexCard(k, v))}
      {Object.entries(dexV).map(([k, v]) => renderDexCard(k, v))}
    </div>
  );

  const renderStress = () => (
    <div style={card}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
        Commitments during volatility events on committed CEX venues.
      </div>
      <div style={grid2}>
        <Field label="Max Spread Expansion Factor" note="≥ 1.0; e.g. 2.0 = spreads at most double.">
          {Txt(stressExp, setStressExp, "2.0")}</Field>
        <Field label="Min Depth Retention (%)">{Txt(stressRet, setStressRet, "60")}</Field>
        <Field label="Recovery Time (minutes)">{Txt(stressRec, setStressRec, "15")}</Field>
        <Field label="Conditions / Definitions">{Txt(stressCond, setStressCond, "e.g. >8% 1h move")}</Field>
      </div>
    </div>
  );

  const renderEconomics = () => (
    <div style={card}>
      {/* Ratified ordering: engagement-level asks first, model second */}
      <div style={grid3}>
        <Field label={`Token Loan Request (${info?.token_symbol ?? "tokens"})`}
          note={hasDex ? "For DEX venues this is the base-side inventory ask." : "0 if none requested."}>
          {Txt(tokenLoan, setTokenLoan, "500000")}</Field>
        <Field label="Engagement Term (months)">{Txt(engTerm, setEngTerm, "12")}</Field>
        <Field label="Termination Notice (days)">{Txt(termNotice, setTermNotice, "30")}</Field>
      </div>

      <div style={{ borderTop: `1px solid ${BORDER}`, margin: "6px 0 16px" }} />
      <label style={lbl}>Compensation Model</label>
      <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
        {permittedModels.map((m) => Radio("comp", m, compModel, setCompModel,
          m === "retainer" ? "Retainer" : m === "call_option" ? "Call Option"
            : m === "profit_share" ? "Profit Share" : m))}
      </div>

      {compModel === "retainer" && (
        <div style={grid2}>
          <Field label="Monthly Retainer (USD)">{Txt(retainerUsd, setRetainerUsd, "15000")}</Field>
        </div>
      )}
      {compModel === "call_option" && (
        <div style={grid2}>
          <Field label="Option Premium (% of notional)">{Txt(optPremiumPct, setOptPremiumPct, "0")}</Field>
          <Field label="Strike Price Derivation" note="e.g. 30d TWAP at grant + 25%">
            {Txt(strikeDeriv, setStrikeDeriv)}</Field>
          <Field label="Number of Tranches">{Txt(numTranches, setNumTranches, "4")}</Field>
          <Field label="Tranche Length (months)">{Txt(trancheLen, setTrancheLen, "6")}</Field>
        </div>
      )}
      {compModel === "profit_share" && (<>
        <div style={grid3}>
          <Field label="Fee Split to Client (%)" note="Share of LP fee income remitted to the foundation.">
            {Txt(psSplit, setPsSplit, "30")}</Field>
          <Field label="Hurdle Rate (%, optional)" note="Fee APR below which no split is owed.">
            {Txt(psHurdle, setPsHurdle)}</Field>
          <Field label="Performance Fee on Quote Accumulation (%, optional)">
            {Txt(psPerfFee, setPsPerfFee)}</Field>
        </div>
        <div style={grid3}>
          <Field label="Impermanent Loss Responsibility">
            <select style={inp} value={psIlResp} onChange={(e) => setPsIlResp(e.target.value)}>
              <option value="">— select —</option>
              <option value="client">Client bears IL</option>
              <option value="mm">MM bears IL</option>
              <option value="shared">Shared</option>
            </select></Field>
          {psIlResp === "shared" && (
            <Field label="Client Share of IL (%)">{Txt(psIlShared, setPsIlShared, "50")}</Field>)}
          <Field label="IL Benchmark" note="Hold = vs holding the assets; Rebalancing = vs a rebalanced portfolio.">
            <select style={inp} value={psIlBench} onChange={(e) => setPsIlBench(e.target.value)}>
              <option value="">— select —</option>
              <option value="hold">Hold benchmark</option>
              <option value="rebalancing">Rebalancing benchmark</option>
            </select></Field>
          <Field label="Retainer Floor (USD/month, optional)">{Txt(psFloor, setPsFloor)}</Field>
        </div>
      </>)}

      {hasDex && (<>
        <div style={{ borderTop: `1px solid ${BORDER}`, margin: "6px 0 16px" }} />
        <label style={lbl}>DEX Inventory — what you are asking the foundation to fund</label>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 10 }}>
          Base-side ask is the Token Loan Request above. CLAMM positions require both legs —
          state the quote-side plan explicitly; own-capital commitments are scored favorably.
        </div>
        {Check(quoteReq, () => setQuoteReq(!quoteReq), "Requesting quote-side inventory (WETH/stable) from the foundation")}
        {quoteReq && <div style={{ ...grid2, marginTop: 8 }}>
          <Field label="Quote-Side Loan (USD equivalent)">{Txt(quoteLoan, setQuoteLoan, "60000")}</Field>
        </div>}
        {Check(ownCap, () => setOwnCap(!ownCap), "Committing own capital to the pool")}
        {ownCap && <div style={{ ...grid3, marginTop: 8 }}>
          <Field label="Own Capital (USD)">{Txt(ownCapUsd, setOwnCapUsd)}</Field>
          <Field label="Composition" note="e.g. 50/50 SAFE/WETH">{Txt(ownCapComp, setOwnCapComp)}</Field>
          <Field label="Lockup (months)">{Txt(ownCapLock, setOwnCapLock)}</Field>
        </div>}
      </>)}

      <div style={{ borderTop: `1px solid ${BORDER}`, margin: "6px 0 16px" }} />
      <Field label="Additional Fees (free text, optional)"
        note="Any charge not captured above. Undisclosed fees discovered later are grounds for termination.">
        {Area(addlFees, setAddlFees, 2)}</Field>
    </div>
  );

  const API_DATA_OPTS = ["order_state", "fills", "inventory"];
  const REPORT_PRESETS = ["Spread compliance", "Depth compliance", "Uptime",
    "Inventory levels", "Volume statistics", "Incident reports"];
  const renderTransparency = () => (
    <div style={card}>
      <label style={lbl}>Read-Only API Access for Mandate Verification</label>
      <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, color: TEXT, display: "flex", gap: 8, cursor: "pointer" }}>
          <input type="radio" checked={apiOffered === true} onChange={() => setApiOffered(true)} /> Offered</label>
        <label style={{ fontSize: 13, color: TEXT, display: "flex", gap: 8, cursor: "pointer" }}>
          <input type="radio" checked={apiOffered === false} onChange={() => setApiOffered(false)} /> Not offered</label>
      </div>
      {apiOffered === true && (<>
        <div style={grid2}>
          <Field label="Access Type">
            <select style={inp} value={apiType} onChange={(e) => setApiType(e.target.value)}>
              <option value="">— select —</option>
              <option value="real_time">Real-time</option>
              <option value="near_real_time">Near real-time</option>
              <option value="daily">Daily</option>
            </select></Field>
          <Field label="Data Available">
            {API_DATA_OPTS.map((o) => Check(apiData.includes(o),
              () => setApiData(apiData.includes(o) ? apiData.filter((x) => x !== o) : [...apiData, o]), o))}
          </Field>
        </div>
        <Field label="Additional Details (endpoints, docs, auth — optional)">
          {Area(apiNotes, setApiNotes, 3)}</Field>
        <div style={{ fontSize: 12, color: MUTED }}>
          Periodic reporting is not requested when read-only API access is provided —
          the mandate is verified directly from the API.
        </div>
      </>)}
      {apiOffered === false && (<>
        <div style={{ borderTop: `1px solid ${BORDER}`, margin: "6px 0 16px" }} />
        <div style={grid3}>
          <Field label="Reporting Frequency">
            <select style={inp} value={repFreq} onChange={(e) => setRepFreq(e.target.value)}>
              <option value="">— select —</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="on_demand">On demand</option>
            </select></Field>
          <Field label="Delivery Method">
            <select style={inp} value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
              <option value="">— select —</option>
              <option value="dashboard">Dashboard access</option>
              <option value="email">Email</option>
              <option value="shared_drive">Shared drive</option>
              <option value="api_push">API push</option>
            </select></Field>
          {deliveryMethod !== "dashboard" && (
            <Field label="Data Formats">
              {["json", "csv"].map((o) => Check(dataFormats.includes(o),
                () => setDataFormats(dataFormats.includes(o) ? dataFormats.filter((x) => x !== o) : [...dataFormats, o]), o.toUpperCase()))}
            </Field>
          )}
        </div>
        <Field label="Report Contents">
          {REPORT_PRESETS.map((o) => Check(repChecks.includes(o),
            () => setRepChecks(repChecks.includes(o) ? repChecks.filter((x) => x !== o) : [...repChecks, o]), o))}
        </Field>
        <Field label="Additional Contents (one per line, optional)">
          {Area(repContents, setRepContents, 2)}</Field>
      </>)}
    </div>
  );

  const renderOperational = () => (
    <div style={card}>
      <div style={grid2}>
        <Field label="Years Active">{Txt(years, setYears, "5")}</Field>
        <Field label="Team Size">{Txt(teamSize, setTeamSize, "12")}</Field>
        <Field label="CEX Venues Covered (comma-separated)">{Txt(cexCovered, setCexCovered, "coinbase, kraken, …")}</Field>
        <Field label="DEX Venues Covered (comma-separated)">{Txt(dexCovered, setDexCovered, "uniswap_v3, aerodrome, …")}</Field>
      </div>
      <Field label="Infrastructure">{Area(infra, setInfra, 3)}</Field>
      <Field label="Risk Management">{Area(risk, setRisk, 3)}</Field>
      <Field label="Regulatory Status">{Area(regStatus, setRegStatus, 3)}</Field>
    </div>
  );

  const renderNarrative = () => (
    <div style={card}>
      <Field label="Executive Summary">{Area(narrSummary, setNarrSummary, 4)}</Field>
      <Field label="Approach Description">{Area(narrApproach, setNarrApproach, 5)}</Field>
      <Field label="Risk Assessment">{Area(narrRisk, setNarrRisk, 4)}</Field>
      <Field label="Value Proposition">{Area(narrValue, setNarrValue, 4)}</Field>
    </div>
  );

  const renderReview = () => {
    const Row = ({ k, v }: { k: string; v: any }) => (
      <div style={{ display: "flex", fontSize: 12, padding: "4px 0",
        borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ width: "var(--m-label-w, 200px)", color: MUTED, flexShrink: 0 }}>{k}</div>
        <div style={{ color: TEXT }}>{v}</div></div>
    );
    const Sect = ({ title, children }: { title: string; children: any }) => (
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT, letterSpacing: 0.8,
          textTransform: "uppercase", marginBottom: 10 }}>{title}</div>
        {children}</div>
    );
    const VenueHead = ({ name, tag }: { name: string; tag: string }) => (
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, margin: "12px 0 4px" }}>
        {name} <span style={{ fontSize: 10, color: tag === "DEX" ? AMBER : ACCENT,
          border: `1px solid ${tag === "DEX" ? AMBER : ACCENT}`, borderRadius: 4,
          padding: "1px 6px", marginLeft: 8 }}>{tag}</span></div>
    );
    const trunc = (s: string, n = 140) => s.length > n ? s.slice(0, n) + "…" : s;
    const modelDetail = compModel === "retainer"
      ? `retainer — $${retainerUsd || "—"}/mo`
      : compModel === "call_option"
        ? `call option — premium ${optPremiumPct || "—"}%, ${numTranches || "—"} tranches × ${trancheLen || "—"} mo`
        : compModel === "profit_share"
          ? `profit share — ${psSplit || "—"}% of fees to client · IL: ${psIlResp || "—"}${psIlResp === "shared" ? ` (client ${psIlShared || "—"}%)` : ""}${psIlBench ? ` · ${psIlBench} benchmark` : ""}${psFloor ? ` · floor $${psFloor}/mo` : ""}`
          : "—";
    return (
      <div>
        {allErrors.length > 0 && (
          <div style={{ ...card, borderColor: RED, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: RED, marginBottom: 8 }}>
              Resolve before submitting</div>
            {allErrors.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: TEXT, padding: "3px 0" }}>
                <span style={{ color: MUTED }}>{e.step}:</span> {e.msg}</div>))}
          </div>
        )}
        <Sect title="Proposer">
          <Row k="Entity" v={entity || "—"} />
          <Row k="Contact" v={`${contact || "—"}${email ? ` · ${email}` : ""}`} />
          <Row k="Jurisdiction" v={jurisdiction || "—"} />
        </Sect>
        <Sect title="Venue Commitments — all depth per side">
          {selectedCex.map(([k, v]) => (
            <div key={k}>
              <VenueHead name={k} tag="CEX" />
              <Row k="Max quoted spread" v={`${v.max_spread_bps || "—"} bps`} />
              <Row k="Uptime" v={`${v.uptime_pct || "—"}%`} />
              <Row k="Depth ±50 / 100 / 200bps" v={`$${v.d50 || "—"} / $${v.d100 || "—"} / $${v.d200 || "—"}`} />
              {v.notes && <Row k="Notes" v={trunc(v.notes)} />}
            </div>))}
          {selectedDex.map(([k, v]) => (
            <div key={k}>
              <VenueHead name={k} tag="DEX" />
              <Row k="Effective spread" v={v.clips.filter((c) => c.clip_usd).map((c) =>
                `$${c.clip_usd} ≤ ${c.max_all_in_bps} bps`).join("  ·  ") || "—"} />
              {Object.entries(v.depth).map(([b, d]) => (
                <Row key={b} k={`Depth ±${b}bps (bid / ask)`} v={`$${d.bid_usd || "—"} / $${d.ask_usd || "—"}`} />))}
              <Row k="In-range uptime" v={`${v.uptime_pct || "—"}%`} />
              <Row k="TVL floor" v={`$${v.tvl_floor_usd || "—"}`} />
              <Row k="Concentration" v={`≥ ${v.concentration_min_pct || "—"}% within band`} />
              {v.mev_protections && <Row k="MEV protections" v={trunc(v.mev_protections)} />}
              {v.notes && <Row k="Notes" v={trunc(v.notes)} />}
            </div>))}
        </Sect>
        {hasCex && (
          <Sect title="Stress Performance">
            <Row k="Spread expansion" v={stressExp ? `≤ ${stressExp}×` : "—"} />
            <Row k="Depth retention" v={stressRet ? `≥ ${stressRet}%` : "—"} />
            <Row k="Recovery" v={stressRec ? `${stressRec} min` : "—"} />
            {stressCond && <Row k="Conditions" v={trunc(stressCond)} />}
          </Sect>
        )}
        <Sect title="Economics">
          <Row k="Model" v={modelDetail} />
          <Row k="Token loan" v={tokenLoan ? `${tokenLoan} ${info?.token_symbol ?? ""}` : "0"} />
          {hasDex && <Row k="Quote-side inventory" v={quoteReq ? `requested — $${quoteLoan || "—"}` : "not requested"} />}
          {hasDex && <Row k="Own capital" v={ownCap ? `$${ownCapUsd || "—"}${ownCapComp ? ` (${ownCapComp})` : ""}${ownCapLock ? `, ${ownCapLock} mo lockup` : ""}` : "none committed"} />}
          <Row k="Engagement term" v={engTerm ? `${engTerm} months` : "—"} />
          <Row k="Termination notice" v={termNotice ? `${termNotice} days` : "—"} />
          {addlFees && <Row k="Additional fees" v={trunc(addlFees)} />}
        </Sect>
        <Sect title="Transparency & Reporting">
          <Row k="Read-only API" v={apiOffered === true
            ? `offered — ${apiType || "—"} (${apiData.join(", ") || "—"})`
            : apiOffered === false ? "not offered" : "—"} />
          {apiOffered === true && apiNotes && <Row k="API details" v={trunc(apiNotes)} />}
          {apiOffered === false && (<>
            <Row k="Reporting" v={`${repFreq || "—"} via ${deliveryMethod || "—"}`} />
            {deliveryMethod !== "dashboard" &&
              <Row k="Formats" v={dataFormats.join(", ").toUpperCase() || "—"} />}
            <Row k="Contents" v={[...repChecks].join(", ") || "—"} />
          </>)}
        </Sect>
        <Sect title="Operational & Narrative">
          <Row k="Experience" v={`${years || "—"} years · team of ${teamSize || "—"}`} />
          <Row k="CEX coverage" v={cexCovered || "—"} />
          <Row k="DEX coverage" v={dexCovered || "—"} />
          {infra && <Row k="Infrastructure" v={trunc(infra)} />}
          {risk && <Row k="Risk management" v={trunc(risk)} />}
          {regStatus && <Row k="Regulatory" v={trunc(regStatus)} />}
          <Row k="Narrative sections" v={[narrSummary && "Executive summary",
            narrApproach && "Approach", narrRisk && "Risk assessment",
            narrValue && "Value proposition"].filter(Boolean).join(", ") || "none provided"} />
        </Sect>
        {rejection && (
          <div style={{ ...card, borderColor: RED, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: RED, marginBottom: 8 }}>
              Rejected by validation (nothing was stored)</div>
            {rejection.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: TEXT, padding: "3px 0" }}>
                <span style={{ color: AMBER }}>{e.rule_id}</span> {e.message}</div>))}
          </div>
        )}
        {submitErr && <div style={{ ...errTxt, marginBottom: 12 }}>{submitErr}</div>}
        <div style={{ display: "flex", gap: 12 }}>
          <button style={btn} onClick={downloadJson}>Download JSON</button>
          <button style={{ ...btnPrimary, opacity: allErrors.length || busy ? 0.5 : 1 }}
            disabled={allErrors.length > 0 || busy} onClick={doSubmit}>
            {busy ? "Submitting…" : "Submit Proposal"}</button>
        </div>
      </div>
    );
  };

    if (loadErr) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex",
      alignItems: "center", justifyContent: "center" }}>
      <div style={{ ...card, maxWidth: 460, borderColor: RED }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: RED, marginBottom: 8 }}>
          This submission link is not available</div>
        <div style={{ fontSize: 13, color: TEXT }}>{loadErr}</div>
      </div>
    </div>
  );
  if (!info) return (
    <div style={{ minHeight: "100vh", background: BG, display: "flex",
      alignItems: "center", justifyContent: "center", color: MUTED, fontSize: 13 }}>
      Loading RFP…</div>
  );
  if (accepted) return (
    <div style={{ minHeight: "100vh", background: BG, padding: "60px 20px",
      display: "flex", justifyContent: "center" }}>
      <div style={{ maxWidth: 640, width: "100%" }}>
        <div style={{ ...card, borderColor: GREEN, marginBottom: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: GREEN, marginBottom: 6 }}>
            Proposal submitted</div>
          <div style={{ fontSize: 13, color: TEXT }}>
            {accepted.proposal_id} · v{accepted.proposal_version} · completeness {accepted.completeness_pct}%
          </div>
        </div>
        {accepted.submission_secret && (
          <div style={{ ...card, borderColor: AMBER, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: AMBER, marginBottom: 8 }}>
              Save your submission secret — it will not be shown again</div>
            <div style={{ fontFamily: "monospace", fontSize: 16, color: TEXT,
              background: "rgba(0,0,0,0.4)", padding: "10px 14px", borderRadius: 6,
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {accepted.submission_secret}
              <button style={{ ...btn, padding: "4px 12px" }}
                onClick={() => navigator.clipboard?.writeText(accepted.submission_secret)}>Copy</button>
            </div>
            <div style={hint}>Needed to check status or view your submission. You may revise
              your proposal by submitting again from this link (versions are tracked).</div>
          </div>
        )}
        {(accepted.cross_validation_warnings?.length > 0 || accepted.validation_warnings?.length > 0) && (
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 8 }}>Review notes</div>
            {[...(accepted.validation_warnings ?? []), ...(accepted.cross_validation_warnings ?? [])].map((w: any, i: number) => (
              <div key={i} style={{ fontSize: 12, color: MUTED, padding: "3px 0" }}>
                <span style={{ color: AMBER }}>{w.rule_id}</span> {w.message}</div>))}
          </div>
        )}
      </div>
    </div>
  );

  const idx = visibleSteps.findIndex((s) => s.id === stepObj.id);
  const curErrs = stepObj.id === "review" ? [] : stepErrors(stepObj.id);
  return (
    <div className="rfp-page" style={{ minHeight: "100vh", background: BG, padding: "36px 20px 80px",
      display: "flex", justifyContent: "center",
      fontFamily: "-apple-system, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ maxWidth: 860, width: "100%" }}>
        <div style={{ marginBottom: 6, fontSize: 11, color: MUTED, letterSpacing: 1.2,
          textTransform: "uppercase" }}>LiquidTick · Liquidity Governance Engine</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: TEXT, marginBottom: 2 }}>
          {info.token_symbol} — Market Making Proposal</div>
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 24 }}>
          {info.rfp_id} · submissions close {new Date(info.expires_at).toUTCString().slice(0, 16)}
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
          {visibleSteps.map((s, i) => (
            <button key={s.id} onClick={() => gotoStep(i)}
              style={{ ...btn, padding: "6px 12px", fontSize: 11,
                borderColor: i === idx ? ACCENT : BORDER,
                color: i === idx ? ACCENT : MUTED }}>
              {s.num} {s.title}</button>))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: "6px 12px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
            <span style={{ color: ACCENT, marginRight: 8 }}>{stepObj.num}</span>{stepObj.title}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {draftNote && <span style={{ fontSize: 11, color: GREEN }}>{draftNote}</span>}
            <button style={{ ...btn, padding: "6px 12px", fontSize: 11 }} onClick={saveDraft}>Save Draft</button>
          </div>
        </div>

        {prefilledFrom && (stepObj.id === "identity" || stepObj.id === "transparency" || stepObj.id === "operational") && (
          <div style={{ border: `1px solid ${ACCENT}`, borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>
            <span style={{ color: ACCENT }}>Pre-filled from your {prefilledFrom} profile.</span>{" "}
            <span style={{ color: MUTED }}>Review and update anything that has changed. Deal-specific steps are blank as usual.</span>
          </div>
        )}
        {prefilledFrom && stepObj.id === "review" && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={updateProfile} onChange={(e) => setUpdateProfile(e.target.checked)} />
            <span>Update my {prefilledFrom} profile with the firm details in this submission</span>
          </label>
        )}
        {stepObj.id === "identity" && renderIdentity()}
        {stepObj.id === "venues" && renderVenues()}
        {stepObj.id === "stress" && renderStress()}
        {stepObj.id === "economics" && renderEconomics()}
        {stepObj.id === "transparency" && renderTransparency()}
        {stepObj.id === "operational" && renderOperational()}
        {stepObj.id === "narrative" && renderNarrative()}
        {stepObj.id === "review" && renderReview()}

        {curErrs.length > 0 && stepObj.id !== "review" && (
          <div style={{ marginTop: 12 }}>
            {curErrs.map((e, i) => <div key={i} style={errTxt}>{e}</div>)}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
          <button style={{ ...btn, visibility: idx === 0 ? "hidden" : "visible" }}
            onClick={() => gotoStep(Math.max(0, idx - 1))}>← Back</button>
          {idx < visibleSteps.length - 1 && (
            <button style={btnPrimary} onClick={() => gotoStep(idx + 1)}>Continue →</button>)}
        </div>
      </div>
    </div>
  );
}

export default SubmitProposalPage;
